import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, 
  Megaphone, 
  Edit2, 
  Trash2,
  FileText,
  Mail,
  ChevronRight,
  Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    script: "",
    email_subject: "",
    email_body: "",
    voice_id: "ErXwobaYiN019PkySvjV"  // Default: Antoni (male voice)
  });

  useEffect(() => {
    fetchCampaigns();
    fetchVoices();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`);
      setCampaigns(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error("Fehler beim Laden der Kampagnen");
    } finally {
      setLoading(false);
    }
  };

  const fetchVoices = async () => {
    try {
      const response = await axios.get(`${API}/voices`);
      setVoices(response.data);
    } catch (error) {
      console.error("Failed to fetch voices:", error);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingCampaign) {
        await axios.put(`${API}/campaigns/${editingCampaign.id}`, formData);
        toast.success("Kampagne aktualisiert");
      } else {
        await axios.post(`${API}/campaigns`, formData);
        toast.success("Kampagne erstellt");
      }
      closeDialog();
      fetchCampaigns();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Kampagne wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/campaigns/${id}`);
      toast.success("Kampagne gelöscht");
      fetchCampaigns();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const openEditDialog = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      script: campaign.script,
      email_subject: campaign.email_subject,
      email_body: campaign.email_body,
      voice_id: campaign.voice_id || "21m00Tcm4TlvDq8ikWAM"
    });
    setStep(1);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setEditingCampaign(null);
    setStep(1);
    setFormData({
      name: "",
      script: "",
      email_subject: "",
      email_body: "",
      voice_id: "ErXwobaYiN019PkySvjV"  // Default: Antoni (male voice)
    });
  };

  const nextStep = () => {
    if (step === 1 && !formData.name) {
      toast.error("Bitte geben Sie einen Namen ein");
      return;
    }
    if (step === 2 && !formData.script) {
      toast.error("Bitte geben Sie ein Skript ein");
      return;
    }
    if (step < 3) setStep(step + 1);
    else handleSubmit();
  };

  const stepTitles = ["Konfiguration", "Anruf-Skript", "E-Mail Template"];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" data-testid="campaigns-title">
            Kampagnen
          </h1>
          <p className="text-slate-500 mt-1">Verwalten Sie Ihre Anrufkampagnen</p>
        </div>
        <Button 
          onClick={() => {
            setEditingCampaign(null);
            setFormData({
              name: "",
              script: "",
              email_subject: "",
              email_body: "",
              voice_id: "ErXwobaYiN019PkySvjV"  // Default: Antoni (male voice)
            });
            setStep(1);
            setShowDialog(true);
          }}
          className="bg-slate-900 hover:bg-slate-800 btn-press"
          data-testid="create-campaign-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Neue Kampagne
        </Button>
      </div>

      {/* Campaigns Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-48 bg-slate-200 animate-pulse rounded-xl"></div>
          ))}
        </div>
      ) : campaigns.length === 0 ? (
        <Card className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)]">
          <CardContent className="py-16">
            <div className="empty-state">
              <Megaphone className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900">Keine Kampagnen</h3>
              <p className="text-slate-500 mt-2 max-w-sm">
                Erstellen Sie Ihre erste Kampagne mit einem Anruf-Skript und E-Mail Template
              </p>
              <Button 
                onClick={() => setShowDialog(true)} 
                className="mt-6 bg-slate-900 hover:bg-slate-800"
                data-testid="create-first-campaign-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Erste Kampagne erstellen
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {campaigns.map((campaign) => (
            <Card 
              key={campaign.id}
              className="stat-card bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)] overflow-hidden group"
              data-testid={`campaign-card-${campaign.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                      <Megaphone className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base font-semibold text-slate-900">
                        {campaign.name}
                      </CardTitle>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {new Date(campaign.created_at).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(campaign)}
                      className="h-8 w-8"
                      data-testid={`edit-campaign-${campaign.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(campaign.id)}
                      className="h-8 w-8 text-red-500 hover:text-red-600"
                      data-testid={`delete-campaign-${campaign.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-slate-600 line-clamp-2">
                      {campaign.script}
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Mail className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-slate-600 truncate">
                      {campaign.email_subject}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Campaign Dialog (Multi-Step Wizard) */}
      <Dialog open={showDialog} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="sm:max-w-lg" data-testid="campaign-dialog">
          <DialogHeader>
            <DialogTitle className="">
              {editingCampaign ? "Kampagne bearbeiten" : "Neue Kampagne"}
            </DialogTitle>
          </DialogHeader>
          
          {/* Step Indicator */}
          <div className="flex items-center justify-between mb-6">
            {stepTitles.map((title, index) => (
              <div key={index} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                  step > index + 1 
                    ? 'bg-emerald-500 text-white' 
                    : step === index + 1 
                      ? 'bg-slate-900 text-white' 
                      : 'bg-slate-100 text-slate-400'
                }`}>
                  {step > index + 1 ? <Check className="w-4 h-4" /> : index + 1}
                </div>
                {index < 2 && (
                  <div className={`w-16 h-0.5 mx-2 ${step > index + 1 ? 'bg-emerald-500' : 'bg-slate-200'}`} />
                )}
              </div>
            ))}
          </div>
          
          <p className="text-sm text-slate-500 mb-4 text-center font-medium">
            Schritt {step}: {stepTitles[step - 1]}
          </p>

          {/* Step 1: Config */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Kampagnenname *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="z.B. Produktlaunch 2024"
                  data-testid="campaign-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="voice">KI-Stimme (ElevenLabs)</Label>
                <Select 
                  value={formData.voice_id} 
                  onValueChange={(value) => setFormData({ ...formData, voice_id: value })}
                >
                  <SelectTrigger data-testid="voice-select">
                    <SelectValue placeholder="Stimme auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {voices.map((voice) => (
                      <SelectItem key={voice.voice_id} value={voice.voice_id}>
                        {voice.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 2: Script */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="script">Anruf-Skript *</Label>
                <p className="text-xs text-slate-500">
                  Dieser Text wird von der KI am Telefon vorgelesen
                </p>
                <Textarea
                  id="script"
                  value={formData.script}
                  onChange={(e) => setFormData({ ...formData, script: e.target.value })}
                  placeholder="Guten Tag, mein Name ist... Wir möchten Ihnen unser neues Projekt vorstellen..."
                  rows={6}
                  data-testid="campaign-script-input"
                />
              </div>
            </div>
          )}

          {/* Step 3: Email */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email_subject">E-Mail Betreff *</Label>
                <Input
                  id="email_subject"
                  value={formData.email_subject}
                  onChange={(e) => setFormData({ ...formData, email_subject: e.target.value })}
                  placeholder="Ihre gewünschten Informationen zu..."
                  data-testid="campaign-email-subject-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email_body">E-Mail Inhalt *</Label>
                <p className="text-xs text-slate-500">
                  Verwenden Sie {"{name}"} für den Namen des Empfängers
                </p>
                <Textarea
                  id="email_body"
                  value={formData.email_body}
                  onChange={(e) => setFormData({ ...formData, email_body: e.target.value })}
                  placeholder="Sehr geehrte(r) {name}, vielen Dank für Ihr Interesse..."
                  rows={6}
                  data-testid="campaign-email-body-input"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {step > 1 && (
              <Button type="button" variant="outline" onClick={() => setStep(step - 1)}>
                Zurück
              </Button>
            )}
            <Button 
              onClick={nextStep}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="campaign-next-btn"
            >
              {step === 3 ? (editingCampaign ? "Speichern" : "Erstellen") : "Weiter"}
              {step < 3 && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
